import { useEffect, useState } from "react";
import { Check, Copy, Plus, Radio, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SOURCES = [
  { key: "loxone", label: "Loxone", aide: "Loxone Config → Sortie virtuelle → adresse HTTP appelée sur l'alarme (méthode GET)." },
  { key: "unv", label: "UNV / Uniview", aide: "NVR → Alarme → Notification HTTP : collez l'URL, méthode GET ou POST." },
  { key: "unifi", label: "UniFi", aide: "UniFi Protect → Alertes → Webhook : collez l'URL (POST)." },
];

export default function AdminAlertes() {
  const [endpoints, setEndpoints] = useState([]);
  const [journal, setJournal] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [clients, setClients] = useState([]);
  const [copie, setCopie] = useState(null);
  const [test, setTest] = useState({});

  const load = () => {
    api.get("/admin/alertes/endpoints").then(({ data }) => setEndpoints(data));
    api.get("/admin/alertes/journal").then(({ data }) => setJournal(data));
  };

  useEffect(() => {
    load();
  }, []);

  const chercher = async () => {
    if (recherche.trim().length < 2) return;
    const { data } = await api.get("/admin/clients/search", { params: { q: recherche } });
    setClients(data);
  };

  const creer = async (client) => {
    try {
      await api.post("/admin/alertes/endpoints", { axonaut_id: client.axonaut_id, site_id: "", site_nom: "" });
      toast.success("Clé d'alerte créée");
      setClients([]);
      setRecherche("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Création impossible"));
    }
  };

  const supprimer = async (ep) => {
    if (!window.confirm(`Supprimer la clé de ${ep.libelle} ? Les équipements configurés ne pourront plus alerter.`)) return;
    await api.delete(`/admin/alertes/endpoints/${ep.id}`);
    toast.success("Clé supprimée");
    load();
  };

  const url = (ep, source) => `${API}/alertes/${ep.token}?source=${source}&equipement=&message=`;

  const copier = async (ep, source) => {
    await navigator.clipboard.writeText(url(ep, source));
    setCopie(`${ep.id}-${source}`);
    toast.success("URL copiée");
    setTimeout(() => setCopie(null), 2500);
  };

  const tester = async (ep, source) => {
    setTest((t) => ({ ...t, [ep.id]: "…" }));
    try {
      const { data } = await api.get(`/alertes/${ep.token}`, {
        params: { source, message: "Test depuis le back-office BKPRO", equipement: "Test", dry_run: true },
      });
      setTest((t) => ({ ...t, [ep.id]: `OK · ${data.client} · ${data.site || "site principal"} · ${data.domaine}` }));
      toast.success("Clé valide : l'équipement pourra créer un ticket");
      load();
    } catch (e) {
      setTest((t) => ({ ...t, [ep.id]: "Échec" }));
      toast.error(errMsg(e, "Test échoué"));
    }
  };

  return (
    <div className="space-y-7" data-testid="admin-alertes">
      <div>
        <div className="bk-label mb-2">Supervision</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Alertes équipements</h1>
        <p className="text-sm text-slate-600 mt-2">
          Chaque site reçoit une URL et une clé secrète à coller dans l'équipement. Dès qu'une alarme part, un ticket est
          créé automatiquement (client, site, domaine et matériel pré-remplis) et l'équipe est prévenue. Une même alerte
          répétée est regroupée pendant 30 minutes.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="bk-label">Créer une clé pour un client</div>
        <div className="flex gap-2">
          <Input
            data-testid="alerte-client-search"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && chercher()}
            placeholder="Nom du client…"
            className="h-11"
          />
          <Button data-testid="alerte-search-btn" onClick={chercher} variant="outline" className="h-11">
            Rechercher
          </Button>
        </div>
        {clients.map((c) => (
          <button
            key={c.axonaut_id}
            data-testid={`alerte-client-${c.axonaut_id}`}
            onClick={() => creer(c)}
            className="w-full text-left rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-[#005CFF] flex items-center justify-between"
          >
            <span>{c.nom}</span>
            <Plus size={15} className="text-[#005CFF]" />
          </button>
        ))}
      </div>

      <div className="space-y-3">
        <div className="bk-label">Clés actives ({endpoints.length})</div>
        {endpoints.map((ep) => (
          <div key={ep.id} data-testid={`alerte-endpoint-${ep.id}`} className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{ep.libelle}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {ep.total_alertes || 0} alerte(s) reçue(s)
                  {ep.derniere_alerte ? ` · dernière ${fmtDate(ep.derniere_alerte)}` : " · aucune pour l'instant"}
                </div>
              </div>
              <button data-testid={`alerte-delete-${ep.id}`} onClick={() => supprimer(ep)} className="text-slate-300 hover:text-red-500">
                <Trash2 size={15} />
              </button>
            </div>

            {SOURCES.map((s) => (
              <div key={s.key} className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900">{s.label}</span>
                  <div className="flex gap-2">
                    <button
                      data-testid={`alerte-copy-${ep.id}-${s.key}`}
                      onClick={() => copier(ep, s.key)}
                      className="text-xs font-semibold text-[#005CFF] inline-flex items-center gap-1.5 hover:underline"
                    >
                      {copie === `${ep.id}-${s.key}` ? <Check size={13} /> : <Copy size={13} />} Copier l'URL
                    </button>
                    <button
                      data-testid={`alerte-test-${ep.id}-${s.key}`}
                      onClick={() => tester(ep, s.key)}
                      className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:text-[#005CFF]"
                    >
                      <Zap size={13} /> Tester
                    </button>
                  </div>
                </div>
                <code className="block text-[11px] text-slate-500 break-all font-mono-bk">{url(ep, s.key)}</code>
                <p className="text-xs text-slate-500">{s.aide}</p>
              </div>
            ))}

            {test[ep.id] && (
              <div className="text-xs font-semibold text-emerald-700" data-testid={`alerte-test-result-${ep.id}`}>
                Test : {test[ep.id]}
              </div>
            )}
          </div>
        ))}
        {endpoints.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="alertes-empty">
            Aucune clé pour l'instant. Créez-en une pour votre premier site pilote.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="bk-label flex items-center gap-2">
          <Radio size={13} /> Journal des alertes reçues ({journal.length})
        </div>
        {journal.map((e) => (
          <div key={e.id} data-testid={`alerte-event-${e.id}`} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-900">{e.client}</span>
              <span className="text-xs text-slate-500">{e.site_nom}</span>
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-200 text-slate-600">{e.source}</span>
              {e.dry_run && <span className="text-xs text-slate-400">test</span>}
              {e.doublon && <span className="text-xs text-amber-600">regroupée (30 min)</span>}
              {e.ticket_reference && <span className="font-mono-bk text-xs text-[#005CFF]">{e.ticket_reference}</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {e.equipement ? `${e.equipement} · ` : ""}
              {e.message || "sans message"} · {fmtDate(e.created_at)}
            </div>
          </div>
        ))}
        {journal.length === 0 && <p className="text-sm text-slate-500">Aucune alerte reçue pour l'instant.</p>}
      </div>
    </div>
  );
}
